"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { motion } from "framer-motion";

export type TBillingInterval = "monthly" | "yearly";

interface IBillingIntervalToggleProps {
  value: TBillingInterval;
  onChange: (interval: TBillingInterval) => void;
  className?: string;
}

const OPTIONS: Array<{ value: TBillingInterval; label: string }> = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

/**
 * Monthly / Yearly toggle rendered as a rounded pill pair (redesign visual
 * language). Shows "2 months free (16% off)" only when yearly is selected.
 * Keeps the accessible RadioGroup semantics; the active pill background
 * animates between options.
 */
export function BillingIntervalToggle({
  value,
  onChange,
  className,
}: IBillingIntervalToggleProps): React.ReactElement {
  // Scope element ids and the framer layoutId so multiple toggles on one page
  // (e.g. several billing sections) don't collide.
  const uid = useId();

  return (
    <div className={cn("flex flex-col items-center gap-2.5", className)}>
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as TBillingInterval)}
        aria-label="Billing interval"
        className="inline-flex items-center rounded-full border border-border bg-card p-1 shadow-sm"
      >
        {OPTIONS.map((option) => {
          const isActive = value === option.value;
          const inputId = `billing-${option.value}-${uid}`;
          return (
            <div key={option.value} className="relative">
              {isActive && (
                <motion.span
                  layoutId={`billing-interval-highlight-${uid}`}
                  className="absolute inset-0 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 320, damping: 26 }}
                  aria-hidden="true"
                />
              )}
              <RadioGroupItem
                value={option.value}
                id={inputId}
                className="peer sr-only"
              />
              <Label
                htmlFor={inputId}
                className={cn(
                  "relative block cursor-pointer select-none rounded-full px-5 py-2.5 text-sm font-semibold leading-none transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
                  isActive
                    ? "text-primary-foreground"
                    : "text-foreground hover:text-primary"
                )}
              >
                {option.label}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
      {value === "yearly" && (
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium leading-none text-primary">
          2 months free (16% off)
        </span>
      )}
    </div>
  );
}
